import { type Node, type Edge } from '@xyflow/react';
import { createContext, useContext } from 'react';

import { type CustomNode } from '../node';

type WorkflowContextType = {
  nodes: CustomNode[];
  edges: Edge[];
  selectedNodeId: string | null;
  selectedNodeLevel: number | null;
  newNodeId: string | null;
  nodeSearchQuery: string;
  setNodeSearchQuery: (query: string) => void;
  setNodes: (nodes: Node[]) => void;
  setEdges: (edges: Edge[]) => void;
  updateNode: (nodeId: string, node: Partial<CustomNode['data']>) => void;
  setSelectedNodeId: (nodeId: string | null, clearSearch?: boolean) => void;
  createAndConnectNode: ({
    sourceNodeId,
    shouldSelectNewNode,
  }: {
    sourceNodeId: string;
    shouldSelectNewNode?: boolean;
  }) => void;
  deleteNode: (nodeId: string) => void;
  getSiblingNodes: (nodeId: string) => CustomNode[];
  getParentNode: (nodeId: string) => CustomNode | null;
  getAllParentNodes: (nodeId: string) => CustomNode[];
  getChildrenNodes: (nodeId: string) => CustomNode[];
  handleSubmitApprovalFlow: (nodes: CustomNode[], edges: Edge[]) => void;
  isLeafNode: (nodeId: string) => boolean;
  undo: () => void;
  canUndo: boolean;
  redo: () => void;
  canRedo: boolean;
  registerNodeHeight: (nodeId: string, height: number) => void;
  workflowView: 'condensed' | 'extended';
  setWorkflowView: (view: 'condensed' | 'extended') => void;
};

export const WorkflowContext = createContext<WorkflowContextType | undefined>(
  undefined,
);

export const useWorkflowContext = () => {
  const context = useContext(WorkflowContext);
  if (!context) {
    throw new Error(
      'useWorkflowContext must be used within a WorkflowProvider',
    );
  }
  return context;
};
